/* eslint-disable jsx-a11y/no-autofocus */
import { useStore } from '@nanostores/react'
import { CheckIcon } from 'lucide-react'
import type { FormEvent } from 'react'
import { useCallback, useState } from 'react'
import { useShallow } from 'zustand/shallow'

import { Button } from '@/components/atoms/Button'
import { DialogTitle } from '@/components/atoms/Dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/Select'
import { useScopedI18n } from '@/locales/client'
import type { Country, CountryCode, LangCode } from '@/services/schemas'
import sendToDealer from '@/services/sendToDealer'
import useCartStore from '@/stores/CartStore'
import useConfigDataStore from '@/stores/ConfigDataStore'
import { $countries } from '@/stores/Countries'
import { $country } from '@/stores/Country'
import { $dealers, $isLoadingDealers, $selectedState } from '@/stores/Dealers'
import useDialogsStore from '@/stores/DialogsStore'
import { useHubStore } from '@/stores/HubStore'
import { $language } from '@/stores/Language'
import { $isLoadingStates, $states } from '@/stores/States'

type DealerFormProps = {
  titleText: string
  buttonText: string
  preselectedHubId?: string
}

type FormFieldProps = {
  id: string
  type?: string
  children: string
  required?: boolean
}

const FormField = ({ id, type = 'text', children, required = true }: FormFieldProps) => (
  <div className="flex flex-col items-start">
    <label className="sr-only text-lg font-semibold leading-10 text-black" htmlFor={id}>
      {children}
    </label>
    <input
      type={type}
      name={id}
      id={id}
      placeholder={children}
      className="w-full rounded-full border border-rsf-grey-light px-6 pb-4 pt-[14px] text-base text-black outline-none ring-0 placeholder:text-rsf-grey"
      required={required}
    />
  </div>
)

const TextAreaField = ({ id, children, required = false }: Omit<FormFieldProps, 'type'>) => (
  <div className="flex flex-col items-start">
    <label className="sr-only text-lg font-semibold leading-10 text-black" htmlFor={id}>
      {children}
    </label>
    <textarea
      name={id}
      id={id}
      placeholder={children}
      rows={3}
      className="w-full rounded-2xl border border-rsf-grey-light px-6 pb-4 pt-[14px] text-base text-black outline-none ring-0 placeholder:text-rsf-grey"
      required={required}
    />
  </div>
)

type SelectFieldProps = {
  id: string
  placeholder: string
  options: { value: string, label: string }[]
  value: string
  onChange: (value: string) => void
  required?: boolean
}

const SelectField = ({ id, placeholder, options, value, onChange, required = true }: SelectFieldProps) => (
  <div className="flex flex-col items-start">
    <label className="sr-only" htmlFor={id}>
      {placeholder}
    </label>
    <Select value={value} onValueChange={onChange} required={required}>
      <SelectTrigger className="h-auto w-full rounded-full border border-rsf-grey-light px-6 pb-4 pt-[14px] text-base text-black outline-none ring-0">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    <input type="hidden" name={id} value={value} />
  </div>
)

function DealerForm({ buttonText, titleText, preselectedHubId }: DealerFormProps) {
  const t = useScopedI18n('dealerForm')
  const cartItems = useCartStore(useShallow(store => store.items))
  const totalPerHub = useCartStore(useShallow(store => store.totalPerHub()))
  const closeDialog = useDialogsStore(store => store.close)
  const lang = useStore($language)
  const hubs = useHubStore(state => state.originalHubs)
  const getAll = useConfigDataStore(store => store.getAllItems)

  // Get the selected country from global store
  const selectedCountryCode = useStore($country)
  const countries = useStore($countries)
  const selectedCountry = countries.find(c => c.isocode2 === selectedCountryCode) || null

  // Get states from global store (pre-fetched when country changes)
  const states = useStore($states)
  const isLoadingStates = useStore($isLoadingStates)
  const showStateSelection = selectedCountry?.has_state_selection === '1'

  // Get dealers from global store (pre-fetched when country/state changes)
  const dealers = useStore($dealers)
  const isLoadingDealers = useStore($isLoadingDealers)
  const selectedState = useStore($selectedState)

  const [isDisabled, setIsDisabled] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [selectedHub, setSelectedHub] = useState<string>(preselectedHubId || '')
  const [selectedDealer, setSelectedDealer] = useState<string>('')

  // Handle state selection - updates the global store which triggers dealer fetch
  const handleStateChange = (value: string) => {
    $selectedState.set(value)
    setSelectedDealer('') // Reset dealer when state changes
  }

  const stateOptions = states.map(state => ({
    value: state.state,
    label: state.state,
  }))

  const dealerOptions = dealers.map(dealer => ({
    value: dealer.id,
    label: dealer.name,
  }))

  const hubOptions = hubs.map(hub => ({
    value: hub.id,
    label: `${hub.name} | ${hub.pricing.currency}${hub.pricing.retailprice}`,
  }))

  // Get state placeholder based on country (UK uses "County")
  const getStatePlaceholder = useCallback(() => {
    if (selectedCountryCode === 'GB') {
      return t('chooseCounty')
    }
    return t('chooseState')
  }, [selectedCountryCode, t])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsDisabled(true)
    setIsLoading(true)

    const formData = new FormData(event.target as HTMLFormElement)
    const selectedDealerData = dealers.find(d => d.id === selectedDealer)

    // Filter cart items to only include items from the selected hub
    const filteredCartItems = cartItems.filter(item => item.hubId === selectedHub && item.amount > 0)

    // Build products array from filtered cart items (only selected hub)
    const products = filteredCartItems
      .map(item => ({
        category: item.categoryId,
        RNumber: item.rNumber,
        amount: item.amount,
        isPreselected: item.preselect,
      }))

    // Get only the selected hub's total, mapped to '1' for the single product template
    const selectedHubTotal = totalPerHub[selectedHub] || 0
    const filteredHubConfig = { '1': selectedHubTotal }

    // Filter config images to only include the selected hub
    const filteredConfigImages = getAll().filter(img => img.hubId === selectedHub)

    try {
      await sendToDealer({
        // Dealer form fields
        hub_id: selectedHub,
        customer_firstname: formData.get('firstName') as string,
        customer_lastname: formData.get('lastName') as string,
        customer_email: formData.get('email') as string,
        customer_phonenumber: formData.get('contactNumber') as string || undefined,
        customer_state: selectedCountry as Country,
        dealer_name: selectedDealerData?.name || '',
        dealer_id: selectedDealer,
        comment: formData.get('message') as string || undefined,
        products,
        lang: lang as LangCode,
        isocode: selectedCountryCode as CountryCode,
        // Configuration data - single product for dealer (user selects one hub)
        isSingleProductConfiguration: 1, // Use single product template for dealer emails
        configImages: filteredConfigImages,
        cartItems: filteredCartItems,
        hubConfig: filteredHubConfig,
      })

      setEmailSent(true)
    }
    catch (error) {
      console.error('Failed to send to dealer:', error)
      // TODO: Show error message to user
    }
    finally {
      setIsLoading(false)
      setIsDisabled(false)
    }
  }

  const handleClose = () => {
    closeDialog('sendToDealer')
  }

  if (emailSent) {
    return (
      <section className="flex w-full flex-col items-center gap-1">
        <DialogTitle className="text-center font-inter text-2xl font-bold leading-relaxed text-rsf-black">
          {t('sentTitle')}
          <CheckIcon className="ml-2 inline size-8 text-rsf-green" />
        </DialogTitle>

        <Button className="mt-4 w-fit self-center font-semibold" onClick={handleClose}>
          {t('close')}
        </Button>
      </section>
    )
  }

  // Show message when no dealers are available (after all loading is complete)
  // For countries with state selection: only show after a state is selected and dealers loaded
  // For countries without state selection: show after dealers loaded
  const isStillLoading = isLoadingDealers || (showStateSelection && isLoadingStates)
  const hasRequiredStateSelection = !showStateSelection || (showStateSelection && selectedState)
  const noDealersAvailable = !isStillLoading && dealers.length === 0 && hasRequiredStateSelection
  
  if (noDealersAvailable) {
    return (
      <section className="flex w-full flex-col items-center gap-4 py-8">
        <DialogTitle className="text-center font-inter text-2xl font-bold leading-relaxed text-rsf-black">
          {titleText}
        </DialogTitle>
        <p className="text-center text-rsf-grey-dark">
          {t('noDealersFound')}
        </p>
      </section>
    )
  }

  return (
    <section className="flex w-full flex-col items-center gap-1">
      <DialogTitle
        className="text-center font-inter text-2xl font-bold leading-loose text-rsf-black"
        autoFocus
        tabIndex={0}
      >
        {isLoading ? t('sendingTitle') : titleText}
      </DialogTitle>

      {isLoading
        ? (
            <div className="grid size-full place-items-center py-12">
              <div className="loader size-7" />
            </div>
          )
        : (
            <form className="mt-4 flex w-full flex-col gap-2 px-2 md:px-4" method="get" onSubmit={handleSubmit}>
              {!preselectedHubId && (
                <SelectField
                  id="hubId"
                  placeholder={t('selectConfiguration')}
                  options={hubOptions}
                  value={selectedHub}
                  onChange={setSelectedHub}
                />
              )}
              <FormField id="firstName">{t('firstName')}</FormField>
              <FormField id="lastName">{t('lastName')}</FormField>
              <FormField id="contactNumber" type="tel">{t('contactNumber')}</FormField>
              <FormField id="email" type="email">{t('email')}</FormField>

              {/* State selection - only shown when country has state selection */}
              {showStateSelection && (
                <SelectField
                  id="state"
                  placeholder={isLoadingStates ? t('loading') : getStatePlaceholder()}
                  options={stateOptions}
                  value={selectedState}
                  onChange={handleStateChange}
                />
              )}

              {/* Dealer selection */}
              <SelectField
                id="dealer"
                placeholder={isLoadingDealers ? t('loading') : t('chooseDealer')}
                options={dealerOptions}
                value={selectedDealer}
                onChange={setSelectedDealer}
              />

              <TextAreaField id="message" required={false}>{t('message')}</TextAreaField>

              <p className="mt-2 text-sm text-rsf-grey-dark w-full text-left">{t('copyNote')}</p>

              <Button
                className="mt-4 w-fit self-center font-semibold"
                type="submit"
                disabled={isDisabled || !selectedHub || !selectedDealer}
              >
                {buttonText}
              </Button>
            </form>
          )}
    </section>
  )
}

export default DealerForm
